package org.teddapp.project.sync

import com.tedd.db.TeddDatabase
import kotlinx.coroutines.CoroutineScope
import org.teddapp.project.core.CoreSession
import org.teddapp.project.ui.AppSettings
import org.teddapp.project.ui.settings.SupabaseConnector
import org.teddapp.project.ui.settings.SyncBindings

/**
 * Implémentation multiplateforme de [SupabaseConnector] qui s'appuie sur
 * [createSyncBindings]. Le caller (top-level de chaque plateforme) fournit
 * un `setBindings` qui met à jour son `mutableStateOf<SyncBindings?>`, ce qui
 * déclenche une recomposition et propage les nouvelles credentials à toute l'UI
 * (via `LocalSyncBindings`).
 */
class CommonSupabaseConnector(
    private val db: TeddDatabase,
    private val appSettings: AppSettings,
    private val scope: CoroutineScope,
    private val setBindings: (SyncBindings?) -> Unit,
    /** Session du noyau à équiper du transport une fois la connexion validée. */
    private val coreSession: CoreSession? = null,
) : SupabaseConnector {

    override val isConnected: Boolean
        get() = appSettings.supabaseUrl.isNotBlank() && appSettings.supabaseAnonKey.isNotBlank()

    override val currentUrl: String
        get() = appSettings.supabaseUrl

    override suspend fun connect(url: String, anonKey: String): String? {
        return try {
            // 1. Test réel de connectivité — détecte URL incorrecte, clé invalide,
            //    schéma non appliqué, RLS bloquante. Doit réussir AVANT toute
            //    persistance pour éviter de garder des credentials cassés.
            val testClient = SupabaseRemoteStore.createClient(url, anonKey)
            val testStore = SupabaseRemoteStore(testClient)
            try {
                testStore.ping()
            } catch (e: Throwable) {
                return mapPingError(e)
            }

            // 2. Ping OK — on peut créer les vraies bindings et persister.
            val bindings = createSyncBindings(
                db = db,
                appSettings = appSettings,
                scope = scope,
                supabaseUrl = url,
                supabaseAnonKey = anonKey,
                coreSession = coreSession,
            ) ?: return "Impossible d'initialiser le client Supabase (URL ou clé invalide)"
            appSettings.applySupabaseUrl(url)
            appSettings.applySupabaseAnonKey(anonKey)
            appSettings.applySyncEnabled(true)
            setBindings(bindings)
            null
        } catch (e: Throwable) {
            e.message ?: e::class.simpleName ?: "Erreur inconnue lors de la connexion"
        }
    }

    /** Traduit les erreurs Supabase/HTTP courantes en message FR actionnable. */
    private fun mapPingError(e: Throwable): String {
        val msg = (e.message ?: "").lowercase()
        return when {
            "401" in msg || "unauthorized" in msg ->
                "Clé anon refusée par Supabase. Vérifie l'anon key dans Project Settings → API."
            "404" in msg || "not found" in msg || "does not exist" in msg ->
                "Table \"organization\" absente. Applique d'abord supabase_schema.sql dans le SQL Editor."
            "403" in msg || "permission denied" in msg ->
                "Accès refusé par Row Level Security. Désactive RLS ou ajoute une policy lecture."
            "host" in msg || "resolve" in msg || "unknown" in msg ->
                "URL Supabase introuvable. Format attendu : https://xxxxx.supabase.co"
            "timeout" in msg || "timed out" in msg ->
                "Timeout réseau. Vérifie ta connexion ou ton firewall."
            else -> "Échec du test : ${e.message ?: e::class.simpleName ?: "erreur inconnue"}"
        }
    }

    override fun disconnect() {
        appSettings.applySyncEnabled(false)
        appSettings.applySupabaseUrl("")
        appSettings.applySupabaseAnonKey("")
        setBindings(null)
    }
}
